import React, { useState, useEffect, useCallback } from 'react';
import { Box, TableBody, InputAdornment, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TotalOrgIcon from '../../../../assets/organization/total-organization-icon.svg';
import NewRegIcon from '../../../../assets/organization/new-registration-icon.svg';
import SuspendedOrgIcon from '../../../../assets/organization/suspended-organization-icon.svg';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../../context/SidebarContext';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import CustomTableHeader from '../../../../components/CustomHeader';
import CustomPagination from '../../../../components/CustomPagination';
import apiCall from '../../../../utils/api';
import dayjs from 'dayjs';
import { CircularProgress } from '@mui/material';

import {
  PageWrapper,
  SummaryCardsContainer,
  SummaryCard,
  IconWrapper,
  CardInfo,
  CardTitle,
  CardValue,
  MainContentContainer,
  ContentHeader,
  SectionTitle,
  ActionsGroup,
  SearchField,
  StyledTabs,
  StyledTab,
  TableWrapper,
  OrgTable,
  OrgTableRow,
  OrgTableCell,
  StatusBadge
} from './styles';

const OrganizationsPage: React.FC = () => {
  const navigate = useNavigate();
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  
  const [tabValue, setTabValue] = useState(0);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [orderBy, setOrderBy] = useState('created_on');
  const [loading, setLoading] = useState(false);
  const [statsLoading, setStatsLoading] = useState(false);
  const [orgs, setOrgs] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [counts, setCounts] = useState({
    total_organizations: 0,
    new_registrations: 0,
    suspended_organizations: 0
  });
  const rowsPerPage = 10;

  const fetchStats = async () => {
    setStatsLoading(true);
    try {
      const response = await apiCall<any>('get', '/api/organization/superadmin/statistics/');
      if (response.data && response.data.data) {
        setCounts(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      let status = '';
      if (tabValue === 1) status = 'active';
      if (tabValue === 2) status = 'inactive';

      const ordering = order === 'desc' ? `-${orderBy}` : orderBy;
      const url = `/api/organization/superadmin/list/?page=${page}&search=${searchTerm}&status=${status}&ordering=${ordering}`;
      
      const response = await apiCall<any>('get', url);
      if (response.data && response.data.data) {
        setOrgs(response.data.data.results || []);
        setTotalCount(response.data.data.count || 0);
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  }, [page, searchTerm, tabValue, order, orderBy]);

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchOrganizations();
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchOrganizations]);

  useEffect(() => {
    setActivePage('organizations');
    setPath([
      { title: 'Super Admin', path: '/super-admin' },
      { title: 'Organizations', path: '/super-admin/organizations' },
    ]);
    setIsShow(true);
  }, [setActivePage, setPath, setIsShow]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setPage(1);
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRequestSort = (event: React.MouseEvent<unknown>, property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(1);
  };

  const ORG_COLUMNS = [
    { id: 'org_name', label: 'Organization Name' },
    { id: 'plan_name', label: 'Plan' },
    { id: 'owner_name', label: 'Owner' },
    { id: 'facility_count', label: 'Facilities' },
    { id: 'worker_count', label: 'Workers' },
    { id: 'created_on', label: 'Created On' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <PageWrapper>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: '#333' }}>
        Organization Management
      </Typography>

      <SummaryCardsContainer>
        <SummaryCard>
          <IconWrapper bgcolor="rgba(87, 175, 152, 0.1)">
            <img src={TotalOrgIcon} alt="Total Orgs" style={{ width: '28px', height: '28px' }} />
          </IconWrapper>
          <CardInfo>
            <CardTitle>Total Organizations</CardTitle>
            <CardValue>{statsLoading ? '...' : counts.total_organizations}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <IconWrapper bgcolor="rgba(48, 107, 255, 0.1)">
            <img src={NewRegIcon} alt="New Registrations" style={{ width: '28px', height: '28px' }} />
          </IconWrapper>
          <CardInfo>
            <CardTitle>New Registrations</CardTitle>
            <CardValue>{statsLoading ? '...' : counts.new_registrations}</CardValue>
          </CardInfo>
        </SummaryCard>

        <SummaryCard>
          <IconWrapper bgcolor="rgba(255, 0, 0, 0.1)">
            <img src={SuspendedOrgIcon} alt="Suspended Orgs" style={{ width: '28px', height: '28px' }} />
          </IconWrapper>
          <CardInfo>
            <CardTitle>Suspended Organizations</CardTitle>
            <CardValue>{statsLoading ? '...' : counts.suspended_organizations}</CardValue>
          </CardInfo>
        </SummaryCard>
      </SummaryCardsContainer>

      <MainContentContainer>
        <ContentHeader>
          <SectionTitle>All Organizations</SectionTitle>
          <ActionsGroup>
            <SearchField
              placeholder="Search Organizations"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: '#999', fontSize: '18px' }} />
                  </InputAdornment>
                ),
              }}
            />
          </ActionsGroup>
        </ContentHeader>

        <StyledTabs value={tabValue} onChange={handleTabChange}>
          <StyledTab label="All" />
          <StyledTab label="Active" />
          <StyledTab label="Inactive" />
        </StyledTabs>

        <TableWrapper>
          <OrgTable>
            <CustomTableHeader
              columns={ORG_COLUMNS}
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
            />
            <TableBody>
              {loading ? (
                <OrgTableRow>
                  <OrgTableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    <CircularProgress size={24} sx={{ color: '#5E8E7E' }} />
                  </OrgTableCell>
                </OrgTableRow>
              ) : orgs.length === 0 ? (
                <OrgTableRow>
                  <OrgTableCell colSpan={7} align="center" sx={{ py: 5 }}>
                    No organizations found
                  </OrgTableCell>
                </OrgTableRow>
              ) : orgs.map((org) => (
                <OrgTableRow 
                  key={org.org_id}
                  onClick={() => navigate(`/super-admin/organizations/${org.org_id}`)}
                  sx={{ cursor: 'pointer' }}
                >
                  <OrgTableCell sx={{ fontWeight: 500 }}>{org.org_name}</OrgTableCell>
                  <OrgTableCell>{org.plan_name}</OrgTableCell>
                  <OrgTableCell>{org.owner_name}</OrgTableCell>
                  <OrgTableCell>{org.facility_count}</OrgTableCell>
                  <OrgTableCell>{org.worker_count}</OrgTableCell>
                  <OrgTableCell>{dayjs(org.created_on).format('YYYY-MM-DD')}</OrgTableCell>
                  <OrgTableCell>
                    <StatusBadge status={org.status}>
                      {org.status === 'approved' ? 'Active' : org.status.charAt(0).toUpperCase() + org.status.slice(1)}
                    </StatusBadge>
                  </OrgTableCell>
                </OrgTableRow>
              ))}
            </TableBody>
          </OrgTable>
        </TableWrapper>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Showing {orgs.length} of {totalCount} results
          </Typography>
          <CustomPagination
            totalPages={Math.ceil(totalCount / rowsPerPage)}
            currentPage={page}
            onPageChange={handlePageChange}
          />
        </Box>
      </MainContentContainer>
    </PageWrapper>
  );
};

export default OrganizationsPage;
